'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useCodeBlockStrings } from './code-block-strings'
import { cx } from './cx'
import { type HighlightToken, highlightLine } from './highlight'
import { CheckIcon } from './icons/check-icon'
import { CopyIcon } from './icons/copy-icon'
import { FileCodeIcon } from './icons/file-code-icon'
import type { CodeBlockProps, DiffLine, DiffSegment } from './types'

const COPIED_RESET_MS = 1500

/** Languages whose content is prose — never run the tokenizer on them. */
const PLAIN_LANGUAGES = new Set(['text', 'plaintext', 'markdown', 'md'])

const RED_HATCH =
  'repeating-linear-gradient(45deg, var(--red) 0, var(--red) 1.5px, transparent 1.5px, transparent 3px)'

const TOKEN_CLASSES: Record<HighlightToken['kind'], string | undefined> = {
  string: 'text-orange',
  number: 'text-orange',
  keyword: 'text-primary-ink',
  call: 'font-medium text-ink',
  plain: undefined,
}

function TokenSpans({ tokens }: { tokens: ReadonlyArray<HighlightToken> }) {
  return (
    <>
      {tokens.map((token, index) => {
        const tokenClass = TOKEN_CLASSES[token.kind]
        return tokenClass ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: tokens are positional text runs with no identity
          <span className={tokenClass} key={index}>
            {token.text}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: tokens are positional text runs with no identity
          <span key={index}>{token.text}</span>
        )
      })}
    </>
  )
}

function DiffSegments({
  segments,
  rowKind,
  isPlainText,
}: {
  segments: ReadonlyArray<DiffSegment>
  rowKind: DiffLine['kind']
  isPlainText: boolean
}) {
  return (
    <>
      {segments.map((segment, index) => {
        const content = isPlainText ? (
          segment.text
        ) : (
          <TokenSpans tokens={highlightLine(segment.text)} />
        )
        if (segment.isHighlighted && rowKind !== 'context') {
          const tint = rowKind === 'add' ? 'var(--green)' : 'var(--red)'
          return (
            <span
              className="rounded-[3px]"
              // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional text runs with no identity
              key={index}
              style={{
                background: `color-mix(in srgb, ${tint} 18%, transparent)`,
                padding: '0 2px',
                margin: '0 -1px',
                boxDecorationBreak: 'clone',
                WebkitBoxDecorationBreak: 'clone',
              }}
            >
              {content}
            </span>
          )
        }
        // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional text runs with no identity
        return <span key={index}>{content}</span>
      })}
    </>
  )
}

type NumberedDiffRow = {
  line: DiffLine
  /** Deletes show their old-side number; adds/context show the new side. */
  gutterNumber: number
}

function numberDiffRows(diff: ReadonlyArray<DiffLine>): Array<NumberedDiffRow> {
  let oldLine = 0
  let newLine = 0
  return diff.map((line) => {
    if (line.kind === 'del') {
      oldLine += 1
      return { line, gutterNumber: oldLine }
    }
    if (line.kind === 'add') {
      newLine += 1
      return { line, gutterNumber: newLine }
    }
    oldLine += 1
    newLine += 1
    return { line, gutterNumber: newLine }
  })
}

function DiffBody({
  diff,
  showLineNumbers,
  isPlainText,
}: {
  diff: ReadonlyArray<DiffLine>
  showLineNumbers: boolean
  isPlainText: boolean
}) {
  return (
    <>
      {numberDiffRows(diff).map(({ line, gutterNumber }, index) => {
        const isAdd = line.kind === 'add'
        const isDel = line.kind === 'del'
        return (
          <div
            className={cx(
              'relative items-start',
              showLineNumbers
                ? 'grid grid-cols-[20px_minmax(0,1fr)]'
                : 'grid grid-cols-[minmax(0,1fr)]',
              { 'bg-green-tint': isAdd, 'bg-red-tint': isDel }
            )}
            // biome-ignore lint/suspicious/noArrayIndexKey: diff rows are a positional listing with no identity
            key={index}
          >
            {isAdd || isDel ? (
              <span
                className="absolute inset-y-0 left-0 w-[3px]"
                style={
                  isAdd
                    ? { background: 'var(--green)' }
                    : { backgroundImage: RED_HATCH }
                }
              />
            ) : null}
            {showLineNumbers ? (
              <span
                className={cx('select-none text-center text-[11px]', {
                  'text-green': isAdd,
                  'text-red': isDel,
                  'text-ink-3': !(isAdd || isDel),
                })}
              >
                {gutterNumber}
              </span>
            ) : null}
            <code
              className={cx(
                'whitespace-pre-wrap break-words',
                showLineNumbers ? 'pr-3 pl-1' : 'px-3'
              )}
            >
              <DiffSegments
                isPlainText={isPlainText}
                rowKind={line.kind}
                segments={line.segments}
              />
            </code>
          </div>
        )
      })}
    </>
  )
}

function CodeBody({
  code,
  showLineNumbers,
  isPlainText,
}: {
  code: string
  showLineNumbers: boolean
  isPlainText: boolean
}) {
  return (
    <>
      {code.split('\n').map((line, index) => (
        <div
          className={cx(
            'items-start',
            showLineNumbers
              ? 'grid grid-cols-[20px_minmax(0,1fr)]'
              : 'grid grid-cols-[minmax(0,1fr)]'
          )}
          // biome-ignore lint/suspicious/noArrayIndexKey: lines are a positional listing with no identity
          key={index}
        >
          {showLineNumbers ? (
            <span className="select-none text-center text-[11px] text-ink-3">
              {index + 1}
            </span>
          ) : null}
          <code
            className={cx(
              'whitespace-pre-wrap break-words',
              showLineNumbers ? 'pr-3 pl-1' : 'px-3'
            )}
          >
            {isPlainText ? line : <TokenSpans tokens={highlightLine(line)} />}
          </code>
        </div>
      ))}
    </>
  )
}

/**
 * The one shared code-snippet panel: a light gray editor card with a
 * filename header, copy button (or diff stat), line-number gutter, wrapped
 * lines, and the synchronous 3-color highlighter. Colors ride entirely on
 * the ink/surface token vocabulary, so dark mode (and the admin app's own
 * palette) come from each app's CSS variables.
 */
export function CodeBlock({
  code,
  language,
  filename,
  diff,
  copyValue,
  canCopy = true,
  copyLabel,
  onCopy,
  onCopyError,
  showLineNumbers = true,
  isEmbedded = false,
  viewportClassName,
  className,
}: CodeBlockProps) {
  const strings = useCodeBlockStrings()
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
    },
    []
  )

  const handleCopy = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      onCopyError?.(new Error('Clipboard API not available'))
      return
    }
    navigator.clipboard.writeText(copyValue ?? code).then(
      () => {
        setIsCopied(true)
        onCopy?.()
        if (copiedTimeoutRef.current) {
          clearTimeout(copiedTimeoutRef.current)
        }
        copiedTimeoutRef.current = setTimeout(
          () => setIsCopied(false),
          COPIED_RESET_MS
        )
      },
      (error: unknown) => {
        onCopyError?.(
          error instanceof Error ? error : new Error('Could not copy code')
        )
      }
    )
  }, [code, copyValue, onCopy, onCopyError])

  const label = filename ?? language ?? 'code'
  const isPlainText = !language || PLAIN_LANGUAGES.has(language)
  const isDiff = Boolean(diff && diff.length > 0)
  const addedCount = diff?.filter((line) => line.kind === 'add').length ?? 0
  const removedCount = diff?.filter((line) => line.kind === 'del').length ?? 0

  return (
    <div
      className={cx(
        'w-full overflow-hidden rounded-card bg-surface',
        { 'shadow-card': !isEmbedded },
        className
      )}
      data-slot="code-block"
      translate="no"
    >
      {/* header — file · (diff stat | copy) */}
      <div className="flex h-11 items-center gap-2 border-line border-b px-4 text-[12.5px]">
        <span className="flex min-w-0 flex-1 items-center gap-[7px]">
          <FileCodeIcon className="shrink-0 text-ink-3" />
          <span className="truncate font-mono text-ink leading-none">
            {label}
          </span>
        </span>

        {isDiff ? (
          <span className="flex shrink-0 items-center gap-2 font-mono text-[12px] tabular-nums leading-none">
            <span className="text-green">+{addedCount}</span>
            <span className="text-red">-{removedCount}</span>
          </span>
        ) : (
          <button
            aria-label={copyLabel ?? strings.copyCode}
            className={cx(
              'chat-focus-ring -mr-1 flex h-6 shrink-0 items-center gap-1 rounded-control px-1.5 font-medium text-[12px] transition-colors duration-100',
              {
                'text-green': isCopied,
                'text-ink-3 hover:bg-hover hover:text-ink': !isCopied,
                'pointer-events-none opacity-40': !canCopy,
              }
            )}
            disabled={!canCopy}
            onClick={handleCopy}
            type="button"
          >
            {isCopied ? <CheckIcon /> : <CopyIcon />}
            {isCopied ? strings.copied : strings.copy}
            <span aria-live="polite" className="sr-only">
              {isCopied ? strings.copied : ''}
            </span>
          </button>
        )}
      </div>

      {/* body — line rows wrap; the gutter hairline sits at the 20px column edge */}
      <div
        aria-label={strings.sampleLabel(language)}
        className={cx(
          'chat-focus-ring overflow-auto overscroll-contain',
          viewportClassName ?? 'max-h-[28rem]'
        )}
        role="region"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the overflowing code viewport must accept keyboard focus so arrow keys can scroll it
        tabIndex={0}
      >
        <div className="relative py-3 font-mono text-[12.5px] text-ink-2 leading-[1.65]">
          {showLineNumbers ? (
            <span className="pointer-events-none absolute inset-y-0 left-5 w-px bg-line" />
          ) : null}
          {isDiff && diff ? (
            <DiffBody
              diff={diff}
              isPlainText={isPlainText}
              showLineNumbers={showLineNumbers}
            />
          ) : (
            <CodeBody
              code={code}
              isPlainText={isPlainText}
              showLineNumbers={showLineNumbers}
            />
          )}
        </div>
      </div>
    </div>
  )
}
